import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { auditApi } from '../../services/api';

const ACTION_COLORS = {
  CREATE: { background: '#dcfce7', color: '#16a34a' },
  UPDATE: { background: '#dbeafe', color: '#2563eb' },
  DELETE: { background: '#fee2e2', color: '#dc2626' },
};

const AuditLogModal = ({ tableName, recordId, onClose }) => {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    auditApi.getRecordHistory(tableName, recordId)
      .then(res => { if (!cancelled) setRows(res.data); })
      .catch(err => { if (!cancelled) setError(err?.response?.data?.error || 'Failed to load change history.'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [tableName, recordId]);

  const identifier = rows[0]?.recordIdentifier || `#${recordId}`;
  const title = `Audit Trail — ${identifier}`;

  return (
    <div className="modal-overlay">
      <div className="modal-content dev-plan-modal" style={{ width: '720px', maxWidth: 'none', height: '260px', minWidth: '480px', minHeight: '250px', resize: 'both', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header" style={{ flexShrink: 0 }}>
          <h3>{title}</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        <div className="modal-body" style={{ flex: 1, overflow: 'auto', padding: 'var(--spacing-md)', minHeight: 0 }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--color-gray-400)' }}>
              Loading change history...
            </div>
          )}

          {error && (
            <div style={{ padding: '12px 16px', background: '#fee2e2', color: '#dc2626', borderRadius: 6 }}>
              {error}
            </div>
          )}

          {!loading && !error && rows.length === 0 && (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--color-gray-400)' }}>
              No changes recorded for this record.
            </div>
          )}

          {!loading && !error && rows.length > 0 && (
            <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>User</th>
                  <th style={thStyle}>Action</th>
                  <th style={thStyle}>Field</th>
                  <th style={thStyle}>Old Value</th>
                  <th style={thStyle}>New Value</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
                  const colors = ACTION_COLORS[row.action] || {};
                  return (
                    <tr key={row.id} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{formatDate(row.changedAt)}</td>
                      <td style={{ ...tdStyle, color: 'var(--color-gray-500)', fontSize: 12 }}>{row.changedBy}</td>
                      <td style={tdStyle}>
                        <span style={{ padding: '2px 7px', borderRadius: 4, fontSize: 11, fontWeight: 600, ...colors }}>
                          {row.action}
                        </span>
                      </td>
                      <td style={tdStyle}>{row.fieldName || <span style={{ color: 'var(--color-gray-400)' }}>—</span>}</td>
                      <td style={{ ...tdStyle, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={row.oldValue}>
                        {row.oldValue != null ? truncate(row.oldValue) : <span style={{ color: 'var(--color-gray-400)' }}>—</span>}
                      </td>
                      <td style={{ ...tdStyle, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={row.newValue}>
                        {row.newValue != null ? truncate(row.newValue) : <span style={{ color: 'var(--color-gray-400)' }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

const thStyle = {
  textAlign: 'left',
  padding: '8px 10px',
  fontWeight: 600,
  fontSize: 11,
  color: 'var(--color-gray-500)',
  whiteSpace: 'nowrap',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};
const tdStyle = { padding: '8px 10px', verticalAlign: 'top' };

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const utcStr = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
    return new Date(utcStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return iso; }
}

function truncate(str, max = 60) {
  if (!str) return '';
  return str.length > max ? str.slice(0, max) + '…' : str;
}

export default AuditLogModal;
